import { Observable, Subject, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Injectable } from '@angular/core';

@Injectable()
export class AnnualDetailsNavigationEventService {
    private _subjectAssessment = new Subject<AssessmentNavigationInput>();
    private _subjectAppeal = new Subject<AppealNavigationInput>();
    private _subjectTax = new Subject<TaxNavigationInput>();
    private _subjectRefund = new Subject<TaxNavigationInput>();
    private _subjectFiling = new Subject<FilingNavigationInput>();
    private _refreshData = new Subject<void>();
    private _refreshAnnualYearData = new Subject<void>();
    private _goToGridData = new Subject<void>();
    private _yearsData = new ReplaySubject<number[]>(1);
    private _onEventReset = new Subject<void>();
    private _refreshAccrualDetailsLink = new Subject<boolean>();
    private _addYearRequest = new Subject<AddYearRequest>();

    resetAnnualDetails(): void {
        this._onEventReset.next();
    }

    goToAssessment(input: AssessmentNavigationInput): void {
        this._subjectAssessment.next(input);
    }

    goToAppeal(input: AppealNavigationInput): void {
        this._subjectAppeal.next(input);
    }

    goToTax(input: TaxNavigationInput): void {
        this._subjectTax.next(input);
    }

    goToRefund(input: TaxNavigationInput): void {
        this._subjectRefund.next(input);
    }

    goToFiling(input: FilingNavigationInput): void {
        this._subjectFiling.next(input);
    }

    refreshActivityData(): void {
        this._refreshData.next();
    }

    refreshAnnualYear(): void {
        this._refreshAnnualYearData.next();
    }

    goToGrid(): void {
        this._goToGridData.next();
    }

    setYears(years: number[]): void {
        this._yearsData.next(years);
    }

    refreshAccrualDetailsLink(allPaymentsExcludedFromAccrual: boolean): void {
        this._refreshAccrualDetailsLink.next(allPaymentsExcludedFromAccrual);
    }

    // Asks Annual Details to show the Add Year dialog with the given year selected; resolves when the dialog closes,
    // or immediately when nothing is listening.
    requestAddYear(year: number): Promise<void> {
        return new Promise<void>((resolve) => {
            if (!this._addYearRequest.observed) {
                resolve();
                return;
            }

            this._addYearRequest.next({ year, resolve });
        });
    }

    get assessmentEvent$(): Observable<AssessmentNavigationInput> {
        return this._subjectAssessment.asObservable().pipe(takeUntil(this._onEventReset));
    }

    get appealEvent$(): Observable<AppealNavigationInput> {
        return this._subjectAppeal.asObservable().pipe(takeUntil(this._onEventReset));
    }

    get taxEvent$(): Observable<TaxNavigationInput> {
        return this._subjectTax.asObservable().pipe(takeUntil(this._onEventReset));
    }

    get refundEvent$(): Observable<TaxNavigationInput> {
        return this._subjectRefund.asObservable().pipe(takeUntil(this._onEventReset));
    }

    get filingEvent$(): Observable<FilingNavigationInput> {
        return this._subjectFiling.asObservable().pipe(takeUntil(this._onEventReset));
    }

    get refreshEvent$(): Observable<void> {
        return this._refreshData.asObservable().pipe(takeUntil(this._onEventReset));
    }

    get refreshAnnualYearEvent$(): Observable<void> {
        return this._refreshAnnualYearData.asObservable().pipe(takeUntil(this._onEventReset));
    }

    get goToGridEvent$(): Observable<void> {
        return this._goToGridData.asObservable().pipe(takeUntil(this._onEventReset));
    }

    get yearsChangedEvent$(): Observable<number[]> {
        return this._yearsData.asObservable().pipe(takeUntil(this._onEventReset));
    }

    get refreshAccrualDetailsLinkEvent$(): Observable<boolean> {
        return this._refreshAccrualDetailsLink.asObservable().pipe(takeUntil(this._onEventReset));
    }

    get addYearRequestEvent$(): Observable<AddYearRequest> {
        return this._addYearRequest.asObservable().pipe(takeUntil(this._onEventReset));
    }
}

export interface AddYearRequest {
    year: number;
    resolve: () => void;
}

export class AssessmentNavigationInput {
    constructor(annualYearID: number, annualAssessmentID: number) {
        this.annualYearID = annualYearID;
        this.annualAssessmentID = annualAssessmentID;
    }

    annualYearID: number;
    annualAssessmentID: number;
}

export class AppealNavigationInput {
    constructor(annualYearID: number, annualAssessmentID: number, appealID: number) {
        this.annualYearID = annualYearID;
        this.annualAssessmentID = annualAssessmentID;
        this.appealID = appealID;
    }

    annualYearID: number;
    annualAssessmentID: number;
    appealID: number;
}

export class TaxNavigationInput {
    constructor(annualYearID: number, taxID: number) {
        this.annualYearID = annualYearID;
        this.taxID = taxID;
    }

    annualYearID: number;
    taxID: number;
}

export class FilingNavigationInput {
    constructor(annualYearID: number, filingId: number) {
        this.annualYearID = annualYearID;
        this.filingId = filingId;
    }

    annualYearID: number;
    filingId: number;
}
